import { Classes } from "@blueprintjs/core";
import classnames from "classnames";
import { ObjectFitProperty } from "csstype";
import React from "react";
import styled, { css } from "styled-components";

import { Intent } from "core/components/IntentContext";
import { IViewProps, View } from "core/decorators/View";
import StringUtil from "core/util/StringUtil";
import { iDef } from "core/util/TypeUtils";
import ImageViewConfig from "./ImageViewConfig";

/**
 * @description a view that lets site editors configure things about how an image is displayed.
 * NOTE! size/positioning etc should be passed in the style prop from the parent components.
 * @author chills
 */

interface IImageViewProps extends IViewProps<ImageViewConfig> {
	src: string;
	overlay?: { position: string, src: string };
}

export enum ImageVariant {
	ICON = "icon",
	LOGO = "logo",
	THUMBNAIL = "thumbnail",
	AVATAR = "avatar",
	BANNER = "banner"
}

interface ImageProps {
	overlay?: { position: string, src: string };
	config: ImageViewConfig;
}

const Image = styled.div<ImageProps>`

	${({overlay, config}) => overlay && css`
		&:after {
			background: url(${overlay.src}) no-repeat;
			background-position: ${overlay.position};
			background-size: contain;
			display: block;
			content: "";
			z-index: 120;
			max-height: ${config.maxHeight};
			max-width: ${config.maxWidth};
			min-height: ${config.minHeight};
			min-width: ${config.minWidth};
			height: ${config.height};
			width: ${config.width};
			
		}
	`}
`;

// empty image so the <img> does not render a border when in skeleton mode
const emptyImage = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

@View({id: "image", configClass: ImageViewConfig, variants: ImageVariant})
export class ImageView extends React.PureComponent<IImageViewProps> {

	public render() {
		const { children, config, style, src, intent, overlay } = this.props;

		const url = src || config.defaultSrc || emptyImage;

		const classes = classnames({
			[Classes.SKELETON]: !url || Intent.LOADING === intent,
			"auto-height": this.props.config.autoEqualize
		});

		const uuid = StringUtil.format("%s.%s", this.props.panelInstance, this.props.viewKey);

		if (config.useInherentImageSize) {
			const img = (
				<img
					src={url}
					style={{
						maxHeight: config.maxHeight,
						maxWidth: config.maxWidth,
						minHeight: config.minHeight,
						minWidth: config.minWidth,
						height: config.height,
						width: config.width,
						padding: config.padding,
						borderRadius: config.borderRadius,
						...style || {},
						backgroundColor: config.backgroundColor,
						objectFit: config.objectFit as ObjectFitProperty,
						objectPosition: config.objectPosition
					}}
					className={classes}
					data-uuid={uuid}
				/>
			);

			if (children) {
				return (
					<div style={{position: 'relative', width: config.width}}>
						{img}
						<div style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0}}>
							{children}
						</div>
					</div>
				);
			}

			return img;
		}

		return (
			<Image
				config={config}
				overlay={overlay}
				style={{
					maxHeight: config.maxHeight,
					maxWidth: config.maxWidth,
					minHeight: config.minHeight,
					minWidth: config.minWidth,
					height: config.height,
					width: config.width,
					borderRadius: config.borderRadius,
					...style || {},
					backgroundColor: config.backgroundColor,
					backgroundImage: iDef(url) ? `url(${url})` : undefined,
					backgroundSize: config.objectFit || 'cover',
					backgroundPosition: config.objectPosition || 'center',
					backgroundRepeat: config.backgroundRepeat ? 'repeat' : 'no-repeat'
				}}
				className={classes}
				data-uuid={uuid}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/common/views/image/Image.view.tsx